import { Fragment } from 'react';
import { PrimaryLink } from '@monorepo/base/src/components/buttons/buttons';
import { SelectedLinerButton } from '@monorepo/base/src/components/table/action-liner/liner-buttons';
import { QuickActionButton } from '@monorepo/base/src/components/table/quick-actions-menu/quick-actions-menu';
import { BetweenPagesStateProps } from '@monorepo/tools/src/lib/interfaces/global';
import { JobModel } from '../../models/job.model';
import routes from '../../routes/index.routes';
import { id } from '@monorepo/tools/src/lib/types/primitives';
import { useStores } from '@monorepo/controlled/src/hooks/use-stores';
import { IEntityActions, ITableActions, useDefaultActions } from './default.actions';
import { getTheme } from '@monorepo/tools/src/lib/get-config';
import { IAdminxStore } from '../../stores';
import { DataAttribute, suffixToDataAttr } from '@monorepo/tools/src/lib/models/data-attr.model';
import { useRoute } from '@monorepo/tools/src/lib/hooks/tools/use-route';
import { ITableFiltersActions, TableFilters } from '@monorepo/base/src/components/table/table-filters/table-filters';
import { jobsFilterWithoutRunInfoPrototypes, jobsFilterWithRunInfoPrototypes } from '../../components/filters-menu/filters';
import { IDebugProps } from '@monorepo/tools/src/lib/interfaces/debug';

const theme = getTheme();

interface IJobActions {
	debugProps?: IDebugProps;
	hasRunInfo?: boolean;
}

export function useJobActions(props: IJobActions = {}) {
	const { debugProps } = props;
	const { jobStore, settingsStore } = useStores<IAdminxStore>();
	const { currentRouteWithoutSpecialChars } = useRoute();
	const { dataAttrs } = debugProps || {};

	const filterStore = settingsStore
		.getPages()
		.get(currentRouteWithoutSpecialChars)
		?.tableStore.getFiltersStore(currentRouteWithoutSpecialChars);
	const currentFilters = filterStore?.getCurrentFilters();

	let DefaultAvailableFilters = [...jobsFilterWithoutRunInfoPrototypes];
	if (props.hasRunInfo) {
		DefaultAvailableFilters = [...jobsFilterWithRunInfoPrototypes];
	}

	const onEditRoute = (id: id) => routes.jobs.edit(id);
	const onDelete = (ids: id[]) => jobStore.getCrud().delete(ids[0]);

	const funcActions = {
		onEditRoute,
		onDelete,
	};

	const SelectedActions = (props: ITableActions<JobModel>) => {
		const { rows } = props;
		const entities = rows.map(row => new JobModel(row.original));
		const { EditAction, DeleteAction } = useDefaultActions({ entities, debugProps, ...funcActions });

		return (
			<Fragment>
				<SelectedLinerButton>
					<EditAction
						state={{ isBlockRequest: true } as BetweenPagesStateProps}
						debugProps={{ dataAttrs: suffixToDataAttr('_selected_edit', dataAttrs) }}
					/>
				</SelectedLinerButton>
				<SelectedLinerButton>
					<DeleteAction debugProps={{ dataAttrs: suffixToDataAttr('_selected_delete', dataAttrs) }} />
				</SelectedLinerButton>
			</Fragment>
		);
	};

	const EntityActions = (props: IEntityActions<JobModel>) => {
		const { entity } = props;
		const { EditAction, DeleteAction } = useDefaultActions({ entities: [entity], ...funcActions });

		return (
			<Fragment>
				<QuickActionButton>
					<EditAction
						iconColor={theme.titleColor}
						textColor={theme.titleColor}
						state={{ isBlockRequest: true } as BetweenPagesStateProps}
					/>
				</QuickActionButton>
				<QuickActionButton>
					<DeleteAction iconColor={theme.titleColor} textColor={theme.titleColor} iconSize={'14px'} />
				</QuickActionButton>
			</Fragment>
		);
	};

	const IndexActions = () => {
		return (
			<PrimaryLink
				icon={'plus'}
				debugProps={{ dataAttrs: [new DataAttribute('id', 'create_job')] }}
				to={`${routes.jobs.create()}`}
				width={'100px'}
				state={{ isBlockRequest: true } as BetweenPagesStateProps}>
				Create
			</PrimaryLink>
		);
	};

	const FiltersActions = (props: ITableFiltersActions) => {
		if (!currentFilters) {
			return null;
		}

		return (
			<TableFilters
				{...props}
				availableFilters={DefaultAvailableFilters}
				currentFilters={currentFilters}
				clearAll={() => filterStore?.clearAll()}
				clearFilter={index => filterStore?.clearFilter(index)}
				addFilter={filter => filterStore?.addFilter(filter)}
				editFilter={(index, filter) => filterStore?.editFilter(index, filter)}
			/>
		);
	};

	return {
		SelectedActions,
		EntityActions,
		IndexActions,
		FiltersActions,
	};
}
